import React from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout";
import SEO from "../components/seo";
import TableOfContents from "../components/TableOfContents";
import BlogList from "../components/BlogList";
import { HeaderContent } from "../components/page";
import ContactFooter from "../components/ContactFooter";
import {
  ContenuDynamiqueForm,
  ContenuDynamiqueTexte,
  ContenuDynamiqueConseil,
  ContenuDynamiqueLien,
  ContenuDynamiqueTexteImage,
  ContenuDynamiqueCarte,
  ContenuDynamiqueImage,
} from "../components/dynamic";
import { POSTS_PER_PAGE, getBlogPagePath } from "../utils/blogPaths";

import "./page.scss";
import "../components/TableOfContents.scss";
import FooterContent from "../components/page/FooterContent";

const PageTemplate = ({ data: { previous, next, post, allWpPost, site } }) => {
  const featuredImage = {
    fluid: post.featuredImage?.node?.localFile?.childImageSharp?.fluid,
    alt: post.featuredImage?.node?.alt || ``,
  };

  let src = featuredImage?.fluid?.src || "";
  let acf = post.acfPageInterne;

  const showContactFooter = post.slug !== "contactez-nous";
  const hasDynamicContent = (acf?.contenudynamique?.length ?? 0) > 0;
  const isBlogPage = post.slug === "blog";

  const allBlogPosts = allWpPost?.nodes || [];
  const totalBlogPages = Math.ceil(allBlogPosts.length / POSTS_PER_PAGE);
  const blogPostsPage1 = isBlogPage
    ? allBlogPosts.slice(0, POSTS_PER_PAGE)
    : [];
  const siteUrl = site.siteMetadata.siteUrl.replace(/\/$/, "");

  const blogSeoOverrides =
    isBlogPage && totalBlogPages > 1
      ? {
          next: getBlogPagePath({ page: 2 }),
        }
      : {};

  return (
    <Layout post={post}>
      <SEO
        title={post.title}
        description={post.excerpt}
        post={post}
        seoOverrides={
          isBlogPage
            ? {
                ...blogSeoOverrides,
                canonical: `${siteUrl}/blog/`,
              }
            : {}
        }
      />

      <div className={"page " + post.slug}>
        <section className="section content">
          {src !== null && <HeaderContent acf={acf} post={post} src={src} />}
          {!isBlogPage && hasDynamicContent && (
            <main>
              <div className="content-layout">
                <div className="table-of-contents">
                  <TableOfContents contentData={acf?.contenudynamique} />
                </div>
                <div className="content-main">
                  <div className="inner">
                    {acf?.contenudynamique?.map((data, key) => {
                      switch (data.fieldGroupName) {
                        case "page_Acfpageinterne_Contenudynamique_Texte":
                          return (
                            <div key={key} data-content-index={key}>
                              <ContenuDynamiqueTexte data={data} />
                            </div>
                          );
                        case "page_Acfpageinterne_Contenudynamique_Texteimage":
                          return (
                            <div key={key} data-content-index={key}>
                              <ContenuDynamiqueTexteImage data={data} />
                            </div>
                          );
                        case "page_Acfpageinterne_Contenudynamique_Image":
                          return (
                            <div key={key} data-content-index={key}>
                              <ContenuDynamiqueImage data={data} />
                            </div>
                          );
                        case "page_Acfpageinterne_Contenudynamique_Carte":
                          return (
                            <div key={key} data-content-index={key}>
                              <ContenuDynamiqueCarte data={data} />
                            </div>
                          );
                        case "page_Acfpageinterne_Contenudynamique_Conseil":
                          return (
                            <div key={key} data-content-index={key}>
                              <ContenuDynamiqueConseil data={data} />
                            </div>
                          );
                        /* case "page_Acfpageinterne_Contenudynamique_Lien":
                          return (
                            <div key={key} data-content-index={key}>
                              <ContenuDynamiqueLien data={data} />
                            </div>
                          );*/
                        case "page_Acfpageinterne_Contenudynamique_Form":
                          return (
                            <div key={key} data-content-index={key}>
                              <ContenuDynamiqueForm />
                            </div>
                          );
                        default:
                          return null;
                      }
                    })}
                  </div>
                </div>
              </div>
            </main>
          )}
        </section>
        {isBlogPage && (
          <BlogList
            posts={blogPostsPage1}
            allPosts={allBlogPosts}
            currentPage={1}
            totalPages={totalBlogPages}
            selectedCategory="all"
          />
        )}
        <FooterContent content={acf?.contentfooter} />
      </div>
      {showContactFooter && <ContactFooter />}
    </Layout>
  );
};

export default PageTemplate;

export const pageQuery = graphql`
  query PageById(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
    $previousPostId: String
    $nextPostId: String
  ) {
    # selecting the current post by id
    post: wpPage(id: { eq: $id }) {
      id
      uri
      content
      title
      slug
      pageCategories {
        nodes {
          name
        }
      }
      date(formatString: "MMMM DD, YYYY")
      seo {
        canonical
        title
        metaDesc
        focuskw
        metaRobotsNoindex
        metaRobotsNofollow
        opengraphAuthor
        opengraphDescription
        opengraphTitle
        opengraphDescription
        opengraphImage {
          altText
          sourceUrl
          srcSet
          localFile {
            childImageSharp {
              fluid(maxWidth: 700, quality: 90, toFormat: JPG) {
                src
                ...GatsbyImageSharpFluid_withWebp_noBase64
              }
            }
          }
        }
        opengraphUrl
        opengraphSiteName
        opengraphPublishedTime
        opengraphModifiedTime
        twitterTitle
        twitterDescription
        twitterImage {
          altText
          sourceUrl
          srcSet
          localFile {
            childImageSharp {
              fluid(maxWidth: 700, quality: 90, toFormat: JPG) {
                src
                ...GatsbyImageSharpFluid_withWebp_noBase64
              }
            }
          }
        }
        breadcrumbs {
          url
          text
        }
        cornerstone
        schema {
          pageType
          articleType
          raw
        }
        readingTime
      }
      acfPageInterne {
        contenthead
        contentfooter {
          text
          image {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 412, quality: 90) {
                  src
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }
        contenudynamique {
          ... on WpPage_Acfpageinterne_Contenudynamique_Texte {
            fieldGroupName
            texte
          }
          ... on WpPage_Acfpageinterne_Contenudynamique_Conseil {
            fieldGroupName
            texte
            titre
            image {
              altText
              localFile {
                childImageSharp {
                  fluid(maxHeight: 147, quality: 90) {
                    src
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                  }
                }
              }
            }
            wallpaperSize
            wallpaper {
              localFile {
                childImageSharp {
                  fluid(maxWidth: 700, quality: 80) {
                    src
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                  }
                }
              }
            }
          }
          ... on WpPage_Acfpageinterne_Contenudynamique_Lien {
            fieldGroupName
            texte
            image {
              altText
              localFile {
                childImageSharp {
                  fluid(maxHeight: 147, quality: 90, toFormat: JPG) {
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                    src
                  }
                }
              }
            }
            lien {
              target
              title
              url
            }
          }
          ... on WpPage_Acfpageinterne_Contenudynamique_Form {
            fieldGroupName
          }
          ... on WpPage_Acfpageinterne_Contenudynamique_Texteimage {
            fieldGroupName
            texte
            placementDeLimage
            imageFormat
            image {
              altText
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1000, quality: 100, toFormat: JPG) {
                    src
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                  }
                }
              }
            }
          }
          ... on WpPage_Acfpageinterne_Contenudynamique_Carte {
            fieldGroupName
            texte
            placementDeLaCarte
          }
          ... on WpPage_Acfpageinterne_Contenudynamique_Image {
            fieldGroupName
            placementDeLimage
            image {
              altText
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1000, quality: 100, toFormat: JPG) {
                    src
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                  }
                }
              }
            }
            description
          }
        }
      }

      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid_tracedSVG
              }
            }
          }
        }
      }
    }

    # Query for blog posts
    allWpPost(sort: { fields: [date], order: DESC }) {
      nodes {
        id
        title
        excerpt
        slug
        date
        categories {
          nodes {
            id
            name
            slug
          }
        }
        featuredImage {
          node {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 800, quality: 90) {
                  src
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }
      }
    }

    site {
      siteMetadata {
        siteUrl
      }
    }

    # this gets us the previous post by id (if it exists)
    previous: wpPage(id: { eq: $previousPostId }) {
      uri
      title
    }

    # this gets us the next post by id (if it exists)
    next: wpPage(id: { eq: $nextPostId }) {
      uri
      title
    }
  }
`;
